import { useState, useEffect } from 'react';

/**
 * Hook for loading and managing domain-specific branding configuration
 */
export const useBranding = () => {
  const [branding, setBranding] = useState({
    domain: 'loading',
    site_name: 'SEO Analysis Platform',
    tagline: 'Automated SEO Reports & Analysis',
    logo_url: '/assets/logo.png',
    primary_color: '#2563eb',
    secondary_color: '#1d4ed8',
    favicon_url: '/favicon.ico',
    ga_tracking_id: null
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadBranding();
  }, []);

  const loadBranding = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/api/v1/config/branding', {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to load branding config: ${response.status}`);
      }

      const result = await response.json();

      if (result.success && result.data) {
        setBranding(result.data);

        // Update document title dynamically
        document.title = `${result.data.site_name} | ${result.data.tagline}`;

        // Update favicon if provided
        if (result.data.favicon_url) {
          updateFavicon(result.data.favicon_url);
        }

        // Update CSS custom properties for colors
        if (result.data.primary_color) {
          document.documentElement.style.setProperty('--brand-primary', result.data.primary_color);
        }
        if (result.data.secondary_color) {
          document.documentElement.style.setProperty('--brand-secondary', result.data.secondary_color);
        }

        console.log('[BRANDING] Loaded configuration for:', result.data.domain);
      } else {
        throw new Error('Invalid branding response format');
      }
    } catch (err) {
      console.error('[BRANDING] Failed to load branding configuration:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const updateFavicon = (faviconUrl) => {
    try {
      // Find existing favicon link or create new one
      let faviconLink = document.querySelector("link[rel*='icon']");

      if (!faviconLink) {
        faviconLink = document.createElement('link');
        faviconLink.rel = 'shortcut icon';
        document.head.appendChild(faviconLink);
      }

      faviconLink.href = faviconUrl;
    } catch (err) {
      console.warn('[BRANDING] Failed to update favicon:', err);
    }
  };

  const getBrandColor = (type = 'primary') => {
    return type === 'primary' ? branding.primary_color : branding.secondary_color;
  };

  const getBrandStyle = (type = 'primary') => {
    return {
      color: getBrandColor(type),
      borderColor: getBrandColor(type)
    };
  };

  const getBrandBackgroundStyle = (type = 'primary') => {
    return {
      backgroundColor: getBrandColor(type),
      color: '#ffffff'
    };
  };

  return {
    branding,
    loading,
    error,
    loadBranding,
    getBrandColor,
    getBrandStyle,
    getBrandBackgroundStyle
  };
};